/**
 * THE GLOWING STATUS DOT — one dot, three meanings, used everywhere the
 * product says "this is fine / this is getting tight / this is nearly gone".
 *
 * WHERE IT CAME FROM. This is not a new invention: it is the low-stock dot
 * that has been on the product page since the shop shipped
 * (Pages/ProductDetail/styles.module.css, `.stockBadgeDot` +
 * `@keyframes stockDotPulse`). Same 7px circle, same expanding ring, same
 * 1.6s ease-out timing, same `prefers-reduced-motion` opt-out. It was lifted
 * here — and ProductDetail now renders THIS component — because the moment a
 * second screen needed the same signal, two copies would have started
 * drifting on size, colour and timing, and "the green glowing icon we used
 * before" would have stopped being one thing.
 *
 * WHAT CHANGED IN THE LIFT.
 *
 *  1. It learned the other two colours. The original only ever existed in
 *     amber, because low-stock was the only state that drew one. Level now
 *     selects a semantic token pair (--c-success / --c-warning / --c-error),
 *     never a literal — status colour is deliberately independent of the
 *     per-vertical brand accent, which is itself sometimes orange and
 *     sometimes green.
 *
 *  2. The pulse became FINITE. The original ran `infinite`, which is fine for
 *     a glance at a price row but not for a page somebody sits and reads: a
 *     ring that never stops expanding beside a paragraph is a tic. It now
 *     runs three times and settles into the static halo, which is the part
 *     that actually carries the "glow". Reduced-motion skips straight to the
 *     settled state.
 *
 * COLOUR IS NEVER THE ONLY SIGNAL. This dot is decoration and is always
 * `aria-hidden`; every caller is required to render the meaning as words
 * beside it. A reader who cannot separate the three hues loses nothing,
 * because the sentence next to the dot already said it.
 */
import React from 'react';
import styles from './styles.module.css';

const LEVELS = { success: styles.success, warning: styles.warning, danger: styles.danger };

export default function StatusDot({ level = 'success', pulse = false, className = '', ...rest }) {
  const tone = LEVELS[level] || LEVELS.success;
  return (
    <span
      aria-hidden
      className={`${styles.dot} ${tone}${pulse ? ` ${styles.pulse}` : ''}${className ? ` ${className}` : ''}`}
      {...rest}
    />
  );
}
